'use client';

import type { SettingsSearchEntry } from '@/lib/settings/registry';
import { ArrowRight } from 'lucide-react';
import type { Route } from 'next';
import type { ReadonlyURLSearchParams } from 'next/navigation';

import type { Command } from '../types';

import { BreadcrumbLabel } from '../command-item-renderer';
import { COMMAND_GROUPS } from '../types';

import { SETTINGS_TABS } from '@/lib/constants';
import { resolveIntegration } from '@/lib/integrations/settings-pages';
import { SETTINGS_SEARCH_ENTRIES } from '@/lib/settings/registry';
import { revealSettingsAnchor } from '@/lib/settings/reveal-settings-anchor';
import { buildSettingsPath } from '@/utils/app-paths';

// A page outranks its own settings when both match, so typing a page's name opens the page. Pages
// sit with Go to Settings among the navigation commands, settings below them.
const PAGE_PRIORITY_BASE = 3200;
const SETTING_PRIORITY_BASE = 2000;

export type SettingsCommand = Command & { entry: SettingsSearchEntry };

/** The link to the settings page these search params show, as the index writes page links. */
function getOpenSettingsPagePath(params: ReadonlyURLSearchParams | undefined) {
  const tab = Object.values(SETTINGS_TABS).find((value) => value === params?.get('tab'));
  return buildSettingsPath({
    tab: tab ?? SETTINGS_TABS.GITHUB,
    integration: resolveIntegration(params?.get('integration') ?? null),
  });
}

/** A command for each settings page and setting, for the command palette and settings search. */
export function buildSettingsCommands(push: (href: Route) => void): SettingsCommand[] {
  return SETTINGS_SEARCH_ENTRIES.map((entry, index) => ({
    id: `settings-${entry.id}`,
    entry,
    // Scored on the title. The location matches as a keyword, so a page's name still finds its
    // settings, below the page.
    label: entry.title,
    displayLabel: <BreadcrumbLabel location={entry.location} title={entry.title} />,
    icon: <ArrowRight />,
    action: () => {
      push(entry.href);
      if (entry.anchor) {
        revealSettingsAnchor(entry.anchor);
      }
    },
    keywords: [...entry.keywords, entry.location],
    description: `Open ${entry.location} › ${entry.title}.`,
    examples: entry.keywords.slice(0, 4),
    safeExecution: 'navigation',
    group: COMMAND_GROUPS.SETTINGS.name,
    priority: (entry.anchor ? SETTING_PRIORITY_BASE : PAGE_PRIORITY_BASE) - index,
    // Settings are too many to list in the empty palette. A page hides while it is open.
    ...(entry.anchor
      ? { searchOnly: true }
      : {
          showOn: (path: string, params?: ReadonlyURLSearchParams) =>
            path !== '/settings' || getOpenSettingsPagePath(params) !== entry.href,
        }),
  }));
}
